import React, { useState } from 'react';
import { Check, X, ArrowRight, Layers, Clock, BarChart3, Users, Shield, Briefcase, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { CampaignSelector } from '../components/CampaignSelector';
const fmt = (n: number) => n.toLocaleString('en-US');
export function SummitProduct() {
  const [campaignMode, setCampaignMode] = useState(1);
  const [altBonus, setAltBonus] = useState(false);
  const isPartial = campaignMode === 0;
  const campaigns = isPartial ? 1 : campaignMode;
  const buildFee = isPartial ? 6000 : 6500 + 1000 * (campaignMode - 1);
  const systemFee = 2500;
  const campaignFee = isPartial ? 1250 : 2500 * campaigns;
  const corpRecruiter = isPartial ? 1500 : 3000 * campaigns - 500 * (campaigns - 1);
  const monthlyTotal = systemFee + campaignFee + corpRecruiter;
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 selection:bg-amber-500/30 font-sans" data-id="element-1089">
      {/* 1. HERO */}
      <section className="relative pt-32 pb-8 px-6 md:px-12 max-w-7xl mx-auto text-center" data-id="element-1090">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-1091" />

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-1092">
          <div className="inline-block px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-widest mb-6" data-id="element-1093">
            Embedded Execution
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" data-id="element-1094">
            HIRE Summit
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto leading-relaxed" data-id="element-1095">
            Summit is designed for teams who want recruiting execution handled
            so leadership can stay focused on production and growth.
          </p>
        </motion.div>
      </section>

      {/* 2. PRICING */}
      <section className="pt-4 pb-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-1096">
        <CampaignSelector mode={campaignMode} onChange={setCampaignMode} accent="amber" data-id="element-1097" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1098">
          {/* Build Fee */}
          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl relative overflow-hidden" data-id="element-1099">
            <div className="mb-4" data-id="element-1100">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-1101">
                Build and Implementation (One-Time)
              </div>
              <div className="text-3xl font-bold text-white" data-id="element-1102">
                ${fmt(buildFee)}
              </div>
              <ul className="mt-5 space-y-2.5 pt-4 border-t border-slate-800/50" data-id="element-1103">
                {['Company and opportunity profiling', 'Custom ATS with dashboards, workflows, and job board integrations', 'Culture video and ad copywriting', 'Landing pages and candidate application flows', 'Full funnel design, from ad to onboarding', 'Compensation and role benchmarking by region', 'Pre-launch system stress test', 'Team training and 90-day coaching to optimize output'].map((point, i) => <li key={i} className="flex items-start" data-id="element-1104">
                    <Check className="w-3.5 h-3.5 text-amber-400 mr-2 mt-0.5 shrink-0" data-id="element-1105" />
                    <span className="text-slate-400 text-sm leading-snug" data-id="element-1106">
                      {point}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Monthly Total */}
          <div className="bg-slate-900/40 border border-amber-500/30 p-6 rounded-xl relative" data-id="element-1107">
            <div className="absolute top-0 left-0 w-full h-1 bg-amber-500 rounded-t-xl" data-id="element-1108" />
            <div className="mb-4" data-id="element-1109">
              <div className="text-xs text-amber-400 uppercase tracking-widest mb-2" data-id="element-1110">
                Total Monthly
              </div>
              <div className="text-4xl font-bold text-white mb-1" data-id="element-1111">
                ${fmt(monthlyTotal)}
              </div>
              <div className="text-sm text-slate-500 mb-6" data-id="element-1112">/ month</div>

              <div className="space-y-3 pt-4 border-t border-slate-800/50" data-id="element-1113">
                <div className="flex justify-between text-sm" data-id="element-1114">
                  <span className="text-slate-400" data-id="element-1115">System Fee</span>
                  <span className="text-white font-medium" data-id="element-1116">
                    ${fmt(systemFee)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1117">
                  <span className="text-slate-400" data-id="element-1118">
                    {isPartial ? 'Partial Campaign' : campaigns === 1 ? 'Campaign Management' : `Campaign Management (x${campaigns})`}
                  </span>
                  <span className="text-white font-medium" data-id="element-1119">
                    ${fmt(campaignFee)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1120">
                  <span className="text-slate-400" data-id="element-1121">
                    {campaigns === 1 ? 'Corporate Recruiter' : `Corporate Recruiters (x${campaigns})`}
                  </span>
                  <span className="text-white font-medium" data-id="element-1122">
                    ${fmt(corpRecruiter)}
                  </span>
                </div>
              </div>

              <ul className="mt-5 space-y-2.5 pt-4 border-t border-slate-800/50" data-id="element-1123">
                {['Active ad management and real-time optimization', 'Live applicant screening, 14 hrs/day, 7 days/week', 'Two-day sales school for every new hire', 'Second-round interview execution handled by a US-based corporate recruiter representing YOUR brand', 'Monthly recruiting review and ongoing consulting'].map((point, i) => <li key={i} className="flex items-start" data-id="element-1124">
                    <Check className="w-3.5 h-3.5 text-amber-400 mr-2 mt-0.5 shrink-0" data-id="element-1125" />
                    <span className="text-slate-400 text-sm leading-snug" data-id="element-1126">
                      {point}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Performance Bonuses */}
          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-1127">
            <div className="mb-4" data-id="element-1128">
              <button type="button" onClick={() => setAltBonus(v => !v)} aria-pressed={altBonus} title="Click to toggle bonus structure" className="text-xs text-amber-400 uppercase tracking-widest mb-4 cursor-pointer hover:text-amber-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 rounded" data-id="element-1129">
                Performance Bonuses
              </button>
              <ul className="space-y-3" data-id="element-1130">
                {(altBonus ? [{
                amount: '$500',
                label: 'per rep at 15 days'
              }, {
                amount: '$500',
                label: 'per rep at 45 days'
              }] : [{
                amount: '$250',
                label: 'per rep at launch'
              }, {
                amount: '$250',
                label: 'per rep at 30 days'
              }, {
                amount: '$250',
                label: 'per rep at 60 days'
              }, {
                amount: '$250',
                label: 'per rep at 90 days'
              }]).map((bonus, i) => <li key={i} className="flex items-start" data-id="element-1131">
                    <span className="text-amber-400 mr-3 text-xl font-bold" data-id="element-1132">
                      {bonus.amount}
                    </span>
                    <span className="text-slate-300 text-lg" data-id="element-1133">
                      {bonus.label}
                    </span>
                  </li>)}
              </ul>
              <p className="mt-5 pt-4 border-t border-slate-800/50 text-slate-400 text-sm leading-snug" data-id="element-1134">
                Tied to retention milestones, not just placement. We only win
                when your reps stick.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 p-6 bg-amber-500/5 border border-amber-500/20 rounded-lg max-w-3xl mx-auto" data-id="element-1135">
          <p className="text-amber-400 text-center font-medium" data-id="element-1136">
            Ad spend is paid directly to the job platforms and is separate from
            HIRE's fees.
          </p>
        </div>
      </section>

      {/* 2. THE EXECUTION BOTTLENECK */}
      <section className="py-20 px-6 md:px-12 max-w-4xl mx-auto border-t border-slate-800/50" data-id="element-1137">
        <div className="text-center mb-12" data-id="element-1138">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1139">
            The Execution Bottleneck
          </h2>
          <p className="text-slate-400 text-lg" data-id="element-1140">
            At scale, recruiting doesn't fail because of traffic or systems.
          </p>
        </div>

        <div className="bg-slate-900/30 border border-slate-800 p-8 md:p-12 rounded-xl" data-id="element-1141">
          <h3 className="text-sm text-amber-400 uppercase tracking-widest font-bold mb-6 text-center" data-id="element-1142">
            Execution Constraints
          </h3>
          <ul className="space-y-4 max-w-2xl mx-auto" data-id="element-1143">
            {['Leaders are pulled into interviews', 'Follow-up slows down', 'Calendars bottleneck', 'Recruiting becomes a second job'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-1144">
                <X className="w-5 h-5 text-amber-500/50 mr-3 mt-0.5 flex-shrink-0" data-id="element-1145" />
                <span data-id="element-1146">{item}</span>
              </li>)}
          </ul>
          <p className="text-center text-slate-400 mt-8 pt-8 border-t border-slate-800" data-id="element-1147">
            Summit exists to remove that bottleneck.
          </p>
        </div>
      </section>

      {/* 3. WHAT CHANGES WITH SUMMIT */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-1148">
        <div className="max-w-5xl mx-auto" data-id="element-1149">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-1150">
            What Changes With Summit
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-1151">
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-1152">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-1153">
                <Shield className="w-6 h-6 text-amber-400" data-id="element-1154" />
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1155">Execution Ownership</h3>
              <p className="text-slate-400 text-sm" data-id="element-1156">
                Recruiting execution becomes HIRE's responsibility
              </p>
            </div>
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-1157">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-1158">
                <Users className="w-6 h-6 text-amber-400" data-id="element-1159" />
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1160">Managed Throughput</h3>
              <p className="text-slate-400 text-sm" data-id="element-1161">
                Interview throughput is managed, not improvised
              </p>
            </div>
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-1162">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-1163">
                <Briefcase className="w-6 h-6 text-amber-400" data-id="element-1164" />
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1165">Protected Time</h3>
              <p className="text-slate-400 text-sm" data-id="element-1166">
                Leadership time is protected for production and growth
              </p>
            </div>
          </div>

          <div className="p-6 bg-amber-500/5 border border-amber-500/20 rounded-lg max-w-3xl mx-auto" data-id="element-1167">
            <p className="text-amber-400 font-medium text-center" data-id="element-1168">
              Summit does not change the recruiting system — it changes who runs
              it.
            </p>
          </div>
        </div>
      </section>

      {/* 4. OWNERSHIP MAP */}
      <section className="py-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-1169">
        <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-1170">
          Ownership Map
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16" data-id="element-1171">
          {/* HIRE Owns */}
          <div className="bg-slate-900/40 border border-amber-500/20 rounded-xl p-8 relative overflow-hidden" data-id="element-1172">
            <div className="absolute top-0 left-0 w-full h-1 bg-amber-500" data-id="element-1173" />
            <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-1174">
              <span className="w-2 h-2 rounded-full bg-amber-500 mr-3" data-id="element-1175" />
              HIRE Owns
            </h3>
            <ul className="space-y-4" data-id="element-1176">
              {['Dedicated recruiter capacity (operated and managed by HIRE)', 'Interview execution and scheduling', 'Candidate follow-up and throughput', 'Recruiting execution accountability'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-1177">
                  <Check className="w-5 h-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1178" />
                  <span data-id="element-1179">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* You Own */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-8 relative overflow-hidden" data-id="element-1180">
            <div className="absolute top-0 left-0 w-full h-1 bg-slate-600" data-id="element-1181" />
            <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-1182">
              <span className="w-2 h-2 rounded-full bg-slate-600 mr-3" data-id="element-1183" />
              You Own
            </h3>
            <ul className="space-y-4" data-id="element-1184">
              {['Final hiring approvals', 'Sales leadership and production', 'Culture and performance expectations'].map((item, idx) => <li key={idx} className="flex items-start text-slate-400" data-id="element-1185">
                  <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 mr-3 mt-0.5 flex-shrink-0 flex items-center justify-center" data-id="element-1186">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-500" data-id="element-1187" />
                  </div>
                  <span data-id="element-1188">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-8 text-center" data-id="element-1189">
          <p className="text-slate-400 font-medium" data-id="element-1190">
            Summit transfers execution ownership and staffing risk to HIRE.
          </p>
        </div>
      </section>

      {/* 5. RECRUITER CAPACITY */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-1191">
        <div className="max-w-4xl mx-auto" data-id="element-1192">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-1193">
            Recruiter Capacity
          </h2>

          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-8 md:p-12" data-id="element-1194">
            <div className="space-y-6" data-id="element-1195">
              <div className="flex items-start" data-id="element-1196">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-1197">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-1198" />
                </div>
                <div data-id="element-1199">
                  <h3 className="text-white font-bold mb-2" data-id="element-1200">
                    Trained and Managed by HIRE
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-1201">
                    Recruiters are trained, managed, and quality-controlled by
                    HIRE to maintain consistency and professionalism.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1202">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-1203">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-1204" />
                </div>
                <div data-id="element-1205">
                  <h3 className="text-white font-bold mb-2" data-id="element-1206">
                    Represents Your Brand
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-1207">
                    Recruiters represent the client's brand with professionalism
                    and alignment to company values.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1208">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-1209">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-1210" />
                </div>
                <div data-id="element-1211">
                  <h3 className="text-white font-bold mb-2" data-id="element-1212">
                    Designed for Consistency
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-1213">
                    Capacity is designed to support consistent interview flow,
                    not heroic bursts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. WHO SUMMIT IS FOR / NOT FOR */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-1214">
        <div className="max-w-5xl mx-auto" data-id="element-1215">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-1216">
            Who Summit Is For
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1217">
            <div data-id="element-1218">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-1219">
                <Check className="w-5 h-5 text-amber-500 mr-2" data-id="element-1220" />
                Summit is a fit if you:
              </h3>
              <ul className="space-y-4" data-id="element-1221">
                {['Want recruiting execution off your plate', 'Believe sales leaders should be selling, not interviewing', 'Prefer external ownership over internal hiring', 'Trust HIRE to manage recruiter performance'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-1222">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-3 mt-2" data-id="element-1223" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-1224">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-1225">
                <X className="w-5 h-5 text-slate-500 mr-2" data-id="element-1226" />
                Summit is not a fit if you:
              </h3>
              <ul className="space-y-4" data-id="element-1227">
                {['Want to manage recruiters internally', 'Prefer hands-on control over execution', 'Only want infrastructure or consulting'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-1228">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mr-3 mt-2" data-id="element-1229" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 8. HOW THIS SCALES */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800 text-center" data-id="element-1230">
        <div className="max-w-3xl mx-auto" data-id="element-1231">
          <h2 className="text-2xl font-bold text-white mb-8" data-id="element-1232">
            How This Scales
          </h2>
          <div className="flex flex-col md:flex-row justify-center gap-6 text-left" data-id="element-1233">
            <div className="flex-1 p-6 border border-slate-800 rounded-lg bg-slate-900/40" data-id="element-1234">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-1235">
                Foundation
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1236">Advantage</h3>
              <p className="text-slate-400 text-sm" data-id="element-1237">
                Where most teams start before scaling execution.
              </p>
            </div>
            <div className="flex-1 p-6 border border-amber-500/30 rounded-lg bg-slate-800/30" data-id="element-1238">
              <div className="text-xs text-amber-400 uppercase tracking-widest mb-2" data-id="element-1239">
                Execution
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1240">Summit</h3>
              <p className="text-slate-400 text-sm" data-id="element-1241">
                Recruiting execution handled for you.
              </p>
            </div>
            <div className="flex-1 p-6 border border-slate-800 rounded-lg bg-slate-900/40" data-id="element-1242">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-1243">
                Leadership
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1244">Leadership</h3>
              <p className="text-slate-400 text-sm" data-id="element-1245">
                Add it when finding leadership becomes the bottleneck.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. CTA */}
      <section className="py-32 px-6 md:px-12 text-center border-t border-slate-800/50" data-id="element-1246">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-1247">
          See if Summit is the right execution model for your team.
        </h2>
        <button className="inline-flex items-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg transition-all" data-id="element-1248">
          Discuss Summit
          <ArrowRight className="ml-2 w-5 h-5" data-id="element-1249" />
        </button>
      </section>
    </div>;
}